import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminNotificationItem } from '../../data/notificationsApi'

interface NotificationToastStackProps {
  notifications: AdminNotificationItem[]
  onDismiss: (id: string) => void
  onDismissAll: () => void
}

const getCategoryStyles = (type: string) => {
  switch (type) {
    case 'order_status':
      return {
        badge:
          'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/60',
        badgeText: 'Order Alert',
        iconBg: 'bg-blue-600 dark:bg-blue-500',
        icon: (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
            />
          </svg>
        ),
      }
    case 'kyc_status':
      return {
        badge:
          'bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800/60',
        badgeText: 'KYC Verification',
        iconBg: 'bg-purple-600 dark:bg-purple-500',
        icon: (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
        ),
      }
    case 'payment_status':
      return {
        badge:
          'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/60',
        badgeText: 'Payment Uploaded',
        iconBg: 'bg-emerald-600 dark:bg-emerald-500',
        icon: (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ),
      }
    case 'grievance':
      return {
        badge:
          'bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800/60',
        badgeText: 'Grievance Raised',
        iconBg: 'bg-rose-600 dark:bg-rose-500',
        icon: (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        ),
      }
    default:
      return {
        badge: 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700',
        badgeText: 'System Alert',
        iconBg: 'bg-primary',
        icon: (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
        ),
      }
  }
}

const STACK_OPACITIES = [1, 0.9, 0.75]
const getStackOpacity = (idx: number): number => STACK_OPACITIES[idx] ?? 0.75

const getCardOpacity = (hovered: boolean, visibleInStack: boolean, stackOp: number): number => {
  if (hovered) return 1
  if (visibleInStack) return stackOp
  return 0
}

export function NotificationToastStack({ notifications, onDismiss, onDismissAll }: NotificationToastStackProps) {
  const navigate = useNavigate()
  const [isHovered, setIsHovered] = useState(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  // Auto-dismiss the entire stack together after 6 seconds of inactivity when not hovered
  useEffect(() => {
    if (isHovered || notifications.length === 0) {
      if (timerRef.current) clearTimeout(timerRef.current)
      return () => {
        // No-op cleanup when not active
      }
    }

    timerRef.current = setTimeout(() => {
      onDismissAll()
    }, 6000)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [notifications, isHovered, onDismissAll])

  if (notifications.length === 0) return null

  const handleCardClick = (notification: AdminNotificationItem) => {
    onDismiss(notification.id)

    if (notification.action_url) {
      navigate(notification.action_url)
    }
  }

  const count = notifications.length

  return (
    <div
      className="fixed top-20 right-6 z-50 flex flex-col items-end pointer-events-auto select-none"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ width: '380px' }}
    >
      {/* Header controls shown on hover or when multiple alerts exist */}
      <div
        className={`w-full flex items-center justify-between px-2 mb-2 transition-all duration-300 ${
          isHovered && count > 1 ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
      >
        <span className="text-xs font-bold text-gray-700 dark:text-gray-200 bg-white dark:bg-surface px-3 py-1 rounded-full shadow-md border border-gray-200 dark:border-white/10">
          {count} alerts · Hovering pauses auto-close
        </span>
        <button
          type="button"
          onClick={onDismissAll}
          className="text-xs font-bold text-gray-700 dark:text-gray-200 hover:text-red-600 dark:hover:text-red-400 bg-white dark:bg-surface px-3 py-1 rounded-full shadow-md border border-gray-200 dark:border-white/10 hover:border-red-200 dark:hover:border-red-500/40 transition-colors"
        >
          Clear all
        </button>
      </div>

      {/* Stacked Cards Area */}
      <div
        className={`relative w-full ${isHovered ? 'overflow-y-auto max-h-[78vh] pr-1.5' : 'overflow-visible'}`}
        style={{
          height: isHovered ? `${Math.min(count * 128, 720)}px` : '108px',
          transition: 'height 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {notifications.map((item, index) => {
          const category = getCategoryStyles(item.type)

          // In stacked mode, index 0 is on top; index 1 is tucked behind; index 2 further behind
          // In expanded mode, each card is shifted downward by index * 128px with clear 18px gaps
          const isVisibleInStack = index < 3
          const stackTranslateY = index * 10
          const stackScale = 1 - index * 0.05
          const stackOpacity = getStackOpacity(index)
          const stackZIndex = 50 - index

          const expandedTranslateY = index * 128
          const expandedScale = 1
          const expandedZIndex = 50 - index

          const translateY = isHovered ? expandedTranslateY : stackTranslateY
          const scale = isHovered ? expandedScale : stackScale
          const opacity = getCardOpacity(isHovered, isVisibleInStack, stackOpacity)
          const pointerEvents = isHovered || index === 0 ? 'auto' : 'none'

          return (
            <div
              key={item.id}
              onClick={() => handleCardClick(item)}
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100%',
                transform: `translate3d(0, ${translateY}px, 0) scale(${scale})`,
                transformOrigin: 'top center',
                opacity,
                zIndex: isHovered ? expandedZIndex : stackZIndex,
                pointerEvents,
                transition:
                  'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
              className="bg-white dark:bg-card rounded-2xl border border-gray-200 dark:border-white/10 shadow-xl dark:shadow-[0_12px_36px_rgba(0,0,0,0.5)] overflow-hidden cursor-pointer group backdrop-blur-md"
            >
              <div className="p-3.5 flex items-start gap-3">
                {/* Icon */}
                <div
                  className={`flex-shrink-0 w-9 h-9 rounded-xl ${category.iconBg} flex items-center justify-center shadow-sm`}
                >
                  {category.icon}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1 mb-0.5">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold border ${category.badge}`}
                    >
                      {category.badgeText}
                    </span>
                    <span className="text-[10px] text-gray-400 dark:text-gray-400 font-medium">Just now</span>
                  </div>

                  <h5 className="text-xs font-bold text-gray-900 dark:text-gray-100 truncate leading-snug">
                    {item.title}
                  </h5>

                  <p className="text-[11px] text-gray-600 dark:text-gray-300 line-clamp-1 leading-snug mt-0.5">
                    {item.message}
                  </p>

                  <div className="mt-1 flex items-center gap-1 text-[11px] font-bold text-primary group-hover:underline">
                    <span>View Details</span>
                    <svg
                      className="w-3 h-3 transition-transform group-hover:translate-x-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>

                {/* Close Button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onDismiss(item.id)
                  }}
                  className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Bottom Gradual Decreasing Progress Bar */}
              <div className="w-full h-1 bg-gray-100/80 dark:bg-white/5 overflow-hidden">
                <div
                  className={`h-full ${category.iconBg}`}
                  style={{
                    animation: 'shrinkProgress 6000ms linear forwards',
                    animationPlayState: isHovered ? 'paused' : 'running',
                  }}
                />
              </div>
            </div>
          )
        })}
      </div>

      {/* Pill badge showing count when stacked */}
      {!isHovered && count > 1 && (
        <div className="mt-7 flex items-center gap-1.5 bg-gray-900/90 dark:bg-surface/95 text-white text-[11px] font-bold px-3 py-1 rounded-full shadow-lg border border-white/20 dark:border-white/15 animate-pulse cursor-pointer">
          <span>+{count - 1} more in stack</span>
          <span className="text-[9px] text-gray-300 font-normal">· Hover to expand</span>
        </div>
      )}
    </div>
  )
}
